import type { ReactNode } from "react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { cn } from "@/lib/utils";
import { EYEBROW } from "@/components/marketing";

/** Shared input styling for every auth form field. */
export const AUTH_INPUT_CLASS =
  "w-full rounded-lg border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50";

/** Shared label styling for every auth form field. */
export const AUTH_LABEL_CLASS =
  "mb-1.5 block text-sm font-medium text-foreground";

/**
 * Shared inline banner for auth-form feedback (error or success), used by
 * login/sign-up/verify-email/logout instead of each page hand-rolling its own
 * `bg-destructive/10` / `bg-green-500/10` div. Renders nothing when `message`
 * is falsy, so callers can pass a possibly-empty string/null directly.
 */
export function AuthAlert({
  message,
  tone = "error",
}: {
  message: string | null | undefined;
  tone?: "error" | "success";
}) {
  if (!message) return null;
  return (
    <div
      className={cn(
        "mb-4 rounded-lg px-3.5 py-2.5 text-sm",
        tone === "error"
          ? "bg-destructive/10 text-destructive"
          : "bg-green-500/10 text-green-700 dark:text-green-400",
      )}
    >
      {message}
    </div>
  );
}

interface AuthShellProps {
  eyebrow: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Shared centered-card shell for the auth pages (login, sign-up,
 * verify-email, logout). Functional equivalent of tianpan-v3-web's
 * `AuthShell` (same eyebrow/title/subtitle/body/footer slots), reshaped as a
 * single centered card using the site header/footer instead of the source's
 * two-column brand-panel layout — the brand-panel treatment is decorative and
 * out of scope for wiring the auth flow to tp-backend.
 */
export function AuthShell({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
}: AuthShellProps) {
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main id="main" className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 sm:p-10">
          <div className="mb-8 text-center">
            <p className={cn(EYEBROW, "mb-3")}>{eyebrow}</p>
            <h1 className="font-[family-name:var(--font-grotesk)] text-3xl font-extrabold leading-[1.1] tracking-[-0.03em] text-foreground">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {subtitle}
              </p>
            ) : null}
          </div>

          <div>{children}</div>

          {footer ? (
            <div className="mt-8 flex flex-col items-center gap-2 border-t border-border/50 pt-6 text-sm">
              {footer}
            </div>
          ) : null}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
